import React from 'react'
import { Divider, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material'
import { AppMenu } from '../common/AppMenu'
import { isOiAnalysisView, ViewType, simulatorSecurities } from '../../assets/constants'
import { Security } from '../../proto/generated/security'
import Chart from '../../assets/icons/Chart'
import FinancialData from '../../assets/icons/FinancialData'
import OptionChain from '../../assets/icons/OptionChain'
import ScalperIcon from '../../assets/icons/ScalperIcon'
import IvPlots from '../../assets/icons/IvPlots'
import SimulatorIcon from '../../assets/icons/SimulatorIcon'
import StrategyBuilder from '../../assets/icons/StrategyBuilder'
import SeasonalsIcon from '../../assets/icons/SeasonalsIcon'
import TechnicalsIcon from '../../assets/icons/TechnicalsIcon'
import HeatmapIcon from '../../assets/icons/HeatmapIcon'
import IndexComponentsIcon from '../../assets/icons/IndexComponentsIcon'
import { isCompanySecurity } from '../../util/cashInstrument'

const VIEW_MENU_ICON_SIZE = 24

const VIEW_MENU_LIST_SX = { py: 0.5 } as const

const VIEW_MENU_ITEM_BUTTON_SX = {
    py: '2px',
    px: 1,
    gap: 1,
} as const

const VIEW_MENU_ITEM_ICON_SX = {
    minWidth: VIEW_MENU_ICON_SIZE,
    width: VIEW_MENU_ICON_SIZE,
    m: 0,
    p: 0,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    '& svg': {
        width: VIEW_MENU_ICON_SIZE,
        height: VIEW_MENU_ICON_SIZE,
    },
    '& .MuiSvgIcon-root': {
        fontSize: VIEW_MENU_ICON_SIZE,
    },
    '& svg path[stroke], & svg rect[stroke]': {
        strokeWidth: 1.5,
    },
} as const

const VIEW_MENU_ITEM_TEXT_SX = { m: 0 } as const

const VIEW_MENU_SECTION_HEADER_SX = {
    px: 1,
    lineHeight: '24px',
    display: 'block',
    fontSize: 11,
    opacity: 0.5,
    textTransform: 'uppercase',
} as const

const getViewMenuSectionDividerSx = (mode: 'light' | 'dark') => ({
    my: 0.5,
    backgroundColor: mode === 'dark' ? '#434651' : '#e0e3eb',
})

type ViewMenuItemProps = {
    label: string
    icon: React.ReactNode
    selected?: boolean
    onClick: () => void
}

const ViewMenuItem = ({ label, icon, selected, onClick }: ViewMenuItemProps) => (
    <ListItem disablePadding>
        <ListItemButton selected={selected} onClick={onClick} sx={VIEW_MENU_ITEM_BUTTON_SX}>
            <ListItemIcon sx={VIEW_MENU_ITEM_ICON_SX}>{icon}</ListItemIcon>
            <ListItemText primary={label} sx={VIEW_MENU_ITEM_TEXT_SX} />
        </ListItemButton>
    </ListItem>
)

const ViewMenuSectionHeader = ({ label }: { label: string}) => (
    <Typography color="text.secondary" sx={VIEW_MENU_SECTION_HEADER_SX}>
        {label}
    </Typography>
)

const getSecurityViewMenuFlags = (security: Security | null | undefined) => {
    const isSimulatorSecurity = Boolean(
        security &&
            simulatorSecurities.some(
                (sec) => sec.exchange === security.exchange && sec.code === security.code,
            ),
    )
    const isIndex = security?.instrument === 'IDX'
    const showFoViewItems = Boolean(security?.hasFo)
    const showCandleAnalysisItems = Boolean(security)
    const showHeatmapMenuItem = Boolean(isIndex)
    const showComponentsMenuItem = Boolean(isIndex)

    return {
        // Financials / fundamentals are company (equity) only — not ETF/bonds/index.
        showFinancialsMenuItem: isCompanySecurity(security),
        showFoViewItems,
        showCandleAnalysisItems,
        showHeatmapMenuItem,
        showComponentsMenuItem,
        showAnalysisSection:
            showFoViewItems || isSimulatorSecurity || showCandleAnalysisItems || showHeatmapMenuItem,
        isSimulatorSecurity,
    }
}

type SecurityViewMenuAnchorProps =
    | {
          anchorReference: 'anchorPosition'
          anchorPosition: { top: number; left: number }
      }
    | {
          anchorReference: 'anchorEl'
          anchorEl: HTMLElement | null
      }

export type SecurityViewMenuProps = SecurityViewMenuAnchorProps & {
    open: boolean
    onClose: () => void
    view: ViewType
    onViewSelect: (view: ViewType) => void
    security: Security | null | undefined
    clickAwayIgnoreRefs?: React.RefObject<HTMLElement | null>[]
    id?: string
    dataEventSource?: string
    anchorOrigin?: { vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
    transformOrigin?: { vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
}

export const SecurityViewMenu: React.FC<SecurityViewMenuProps> = ({
    open,
    onClose,
    view,
    onViewSelect,
    security,
    clickAwayIgnoreRefs,
    id,
    dataEventSource,
    anchorOrigin,
    transformOrigin,
    ...anchorProps
}) => {
    const theme = useTheme()
    const sectionDividerSx = getViewMenuSectionDividerSx(theme.palette.mode)
    const {
        showFinancialsMenuItem,
        showFoViewItems,
        showCandleAnalysisItems,
        showHeatmapMenuItem,
        showComponentsMenuItem,
        showAnalysisSection,
        isSimulatorSecurity,
    } = getSecurityViewMenuFlags(security)

    return (
        <AppMenu
            open={open}
            onClose={onClose}
            clickAwayIgnoreRefs={clickAwayIgnoreRefs}
            id={id}
            data-event-source={dataEventSource}
            transitionDuration={0}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            MenuListProps={{ dense: true, sx: VIEW_MENU_LIST_SX }}
            {...anchorProps}
        >
            <ViewMenuSectionHeader label="Trade" />
            <ViewMenuItem
                label="Chart"
                icon={<Chart />}
                selected={view === ViewType.CHART || view === ViewType.SYMBOL || view === ViewType.SPREAD}
                onClick={() => onViewSelect(ViewType.CHART)}
            />
            {showFoViewItems ? (
                <ViewMenuItem
                    label="Scalper"
                    icon={<ScalperIcon />}
                    selected={view === ViewType.SCALPER}
                    onClick={() => onViewSelect(ViewType.SCALPER)}
                />
            ) : null}
            {showFoViewItems ? (
                <ViewMenuItem
                    label="Option Chain"
                    icon={<OptionChain />}
                    selected={view === ViewType.OPTION_CHAIN}
                    onClick={() => onViewSelect(ViewType.OPTION_CHAIN)}
                />
            ) : null}
            {showFoViewItems ? (
                <ViewMenuItem
                    label="Strategy Builder"
                    icon={<StrategyBuilder />}
                    selected={view === ViewType.STRATEGY_BUILDER}
                    onClick={() => onViewSelect(ViewType.STRATEGY_BUILDER)}
                />
            ) : null}

            {showFinancialsMenuItem ? (
                <Divider key="fundamentals-divider" sx={sectionDividerSx} />
            ) : null}
            {showFinancialsMenuItem ? <ViewMenuSectionHeader label="Fundamentals" /> : null}
            {showFinancialsMenuItem ? (
                <ViewMenuItem
                    label="Financials"
                    icon={<FinancialData />}
                    selected={view === ViewType.FINANCIAL_DATA}
                    onClick={() => onViewSelect(ViewType.FINANCIAL_DATA)}
                />
            ) : null}

            {showAnalysisSection ? <Divider key="analysis-divider" sx={sectionDividerSx} /> : null}
            {showAnalysisSection ? <ViewMenuSectionHeader label="Analysis" /> : null}
            {showAnalysisSection && showFoViewItems ? (
                <ViewMenuItem
                    label="OI Analysis"
                    icon={<IvPlots />}
                    selected={isOiAnalysisView(view)}
                    onClick={() => onViewSelect(ViewType.OPEN_INTEREST_CHANGE)}
                />
            ) : null}
            {showCandleAnalysisItems ? (
                <ViewMenuItem
                    label="Seasonals"
                    icon={<SeasonalsIcon />}
                    selected={view === ViewType.SEASONALS}
                    onClick={() => onViewSelect(ViewType.SEASONALS)}
                />
            ) : null}
            {showCandleAnalysisItems ? (
                <ViewMenuItem
                    label="Technicals"
                    icon={<TechnicalsIcon />}
                    selected={view === ViewType.TECHNICALS}
                    onClick={() => onViewSelect(ViewType.TECHNICALS)}
                />
            ) : null}
            {showHeatmapMenuItem ? (
                <ViewMenuItem
                    label="Heatmap"
                    icon={<HeatmapIcon />}
                    selected={view === ViewType.HEATMAP}
                    onClick={() => onViewSelect(ViewType.HEATMAP)}
                />
            ) : null}
            {showComponentsMenuItem ? (
                <ViewMenuItem
                    label="Components"
                    icon={<IndexComponentsIcon />}
                    selected={view === ViewType.INDEX_COMPONENTS}
                    onClick={() => onViewSelect(ViewType.INDEX_COMPONENTS)}
                />
            ) : null}
            {showAnalysisSection && isSimulatorSecurity ? (
                <ViewMenuItem
                    label="Simulator"
                    icon={<SimulatorIcon />}
                    selected={view === ViewType.SIMULATOR}
                    onClick={() => onViewSelect(ViewType.SIMULATOR)}
                />
            ) : null}
        </AppMenu>
    )
}
